import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { ujmsSystemHealth, ujmsIntegrityReport, ujmsBackupExport, ujmsRunNightly, ujmsNightlyRuns } from "../../services/ujmsApi";
import { StoragePanel } from "../enterprise/admin/StoragePanel";
import { Panel, Badge, thCls, tdCls, btnPrimary, btnGhost, fmtDT, cls } from "../ui";
import { HeartPulse, ShieldCheck, Download, Loader2, RefreshCw, MoonStar } from "lucide-react";

export function HealthTab() {
  const [health, setHealth] = useState(null);
  const [integrity, setIntegrity] = useState(null);
  const [busy, setBusy] = useState(false);
  const [backupBusy, setBackupBusy] = useState(false);
  const [nightly, setNightly] = useState([]);
  const [nightlyBusy, setNightlyBusy] = useState(false);

  const loadHealth = useCallback(() => {
    ujmsSystemHealth().then((r) => setHealth(r.data)).catch(() => {});
    ujmsNightlyRuns().then((r) => setNightly(r.data)).catch(() => {});
  }, []);
  useEffect(() => { loadHealth(); }, [loadHealth]);

  const runIntegrity = async () => {
    setBusy(true);
    try {
      const r = await ujmsIntegrityReport();
      setIntegrity(r.data);
      if (r.data.overall === "OK") toast.success("All integrity checks passed");
      else toast.warning(`${r.data.failed} failure(s), ${r.data.warnings} warning(s)`);
    } catch (e) { toast.error(e.response?.data?.detail || "Integrity run failed"); }
    finally { setBusy(false); }
  };

  const backup = async () => {
    setBackupBusy(true);
    try {
      const r = await ujmsBackupExport();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data);
      a.download = `ujms-backup-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      toast.success("Backup snapshot downloaded (audited)");
    } catch { toast.error("Backup export failed"); }
    finally { setBackupBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="health-tab">
      <div className="flex flex-wrap gap-2">
        <button className={btnGhost} onClick={loadHealth} data-testid="health-refresh"><RefreshCw className="w-3.5 h-3.5" /> Refresh Health</button>
        <button className={btnPrimary} disabled={busy} onClick={runIntegrity} data-testid="integrity-run-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} Run Integrity Checks
        </button>
        <button className={btnGhost} disabled={backupBusy} onClick={backup} data-testid="backup-btn">
          {backupBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-3.5 h-3.5" />} Export Backup Snapshot
        </button>
        <button className={btnGhost} disabled={nightlyBusy} onClick={async () => {
          setNightlyBusy(true);
          try {
            const r = await ujmsRunNightly();
            toast.success(`Nightly run: integrity ${r.data.integrity_overall}, ${r.data.reminders_queued} reminder(s) queued${r.data.alerts_created ? " — ADMIN ALERT raised" : ""}`);
            loadHealth();
          } catch (e) { toast.error(e.response?.data?.detail || "Nightly run failed"); }
          finally { setNightlyBusy(false); }
        }} data-testid="nightly-run-btn">
          {nightlyBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <MoonStar className="w-3.5 h-3.5" />} Run Nightly Check Now
        </button>
      </div>

      <Panel title="Nightly auto-check — runs every night at 02:00 court time (integrity scan + GO 1.2 compliance + 48h hearing reminders + admin alerts)" testId="nightly-panel">
        <table className="w-full border-collapse">
          <thead><tr><th className={thCls}>Ran</th><th className={thCls}>Trigger</th><th className={thCls}>Integrity</th><th className={thCls}>Failures</th><th className={thCls}>GO 1.2 Compliance</th><th className={thCls}>Reminders Queued</th><th className={thCls}>Alert Raised</th></tr></thead>
          <tbody>
            {nightly.map((r) => (
              <tr key={r.id} className="hover:bg-slate-50" data-testid={`nightly-row-${r.id}`}>
                <td className={`${tdCls} font-mono text-xs`}>{fmtDT(r.created_at)}</td>
                <td className={tdCls}><Badge value={r.trigger} /></td>
                <td className={tdCls}><Badge value={r.integrity_overall} /></td>
                <td className={`${tdCls} font-mono text-xs`}>{r.integrity_failed} fail / {r.integrity_warnings} warn</td>
                <td className={tdCls} data-testid={`nightly-compliance-${r.id}`}>{r.compliance_overall ? <Badge value={r.compliance_overall} /> : "—"}</td>
                <td className={`${tdCls} font-mono text-xs`}>{r.reminders_queued}</td>
                <td className={`${tdCls} text-xs`}>{r.alerts_created ? <span className="text-red-700 font-semibold">YES ({r.alerts_created})</span> : "—"}</td>
              </tr>
            ))}
            {!nightly.length && <tr><td className={`${tdCls} text-slate-400`} colSpan={7}>No runs yet — the scheduler fires at 02:00, or trigger one now.</td></tr>}
          </tbody>
        </table>
      </Panel>
      <div className="hidden">
      </div>

      {health && (
        <Panel title="System health" testId="health-panel">
          <div className="p-4 space-y-3">
            <div className="flex flex-wrap gap-3 items-center">
              <span className={cls("inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-1 rounded-sm",
                health.database === "UP" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-red-50 text-red-700 border border-red-200")}
                data-testid="health-db-status">
                <HeartPulse className="w-4 h-4" /> Database {health.database}
              </span>
              <span className="text-xs text-slate-500">Checked {fmtDT(health.checked_at)}</span>
              <span className="text-xs text-slate-500">Awaiting retry: <b>{health.deliveries_awaiting_retry}</b></span>
              <span className={cls("text-xs", health.dead_letter_count > 0 ? "text-red-600 font-semibold" : "text-slate-500")}>
                Dead letters: <b>{health.dead_letter_count}</b>
              </span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-2">
              {Object.entries(health.collection_counts).map(([coll, n]) => (
                <div key={coll} className="border border-slate-200 rounded-sm px-2.5 py-1.5">
                  <div className="font-mono text-sm font-semibold text-slate-900">{n}</div>
                  <div className="font-mono text-[10px] text-slate-400 truncate">{coll.replace("ujms_", "")}</div>
                </div>
              ))}
            </div>
            <div className="text-xs text-slate-500">
              Last audit: {health.last_audit_event ? `${health.last_audit_event.action} @ ${fmtDT(health.last_audit_event.timestamp)}` : "—"}
              {" · "}Last case event: {health.last_case_event ? `${health.last_case_event.event_type} @ ${fmtDT(health.last_case_event.timestamp)}` : "—"}
            </div>
          </div>
        </Panel>
      )}

      {integrity && (
        <Panel title={`Referential integrity — ${integrity.total_checks} checks, ran ${fmtDT(integrity.ran_at)}`} testId="integrity-panel">
          <div className="px-4 pt-3">
            <Badge value={integrity.overall} testId="integrity-overall" />
          </div>
          <table className="w-full border-collapse mt-2">
            <thead><tr><th className={thCls}>Check</th><th className={thCls}>Status</th><th className={thCls}>Violations</th><th className={thCls}>Samples</th><th className={thCls}>Rule</th></tr></thead>
            <tbody>
              {integrity.checks.map((c) => (
                <tr key={c.name} className="hover:bg-slate-50" data-testid={`integrity-check-${c.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
                  <td className={`${tdCls} font-medium`}>{c.name}</td>
                  <td className={tdCls}><Badge value={c.status} /></td>
                  <td className={`${tdCls} font-mono`}>{c.count}</td>
                  <td className={`${tdCls} font-mono text-[10px] max-w-xs truncate`}>{c.samples.join(", ") || "—"}</td>
                  <td className={`${tdCls} text-xs text-slate-500`}>{c.detail}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      )}
      <StoragePanel />
      {!integrity && <p className="text-sm text-slate-400">Run the integrity checks to verify foreign keys, ledger invariants, docket continuity and disposition rules across the whole record.</p>}
    </div>
  );
}
